import PropTypes from 'prop-types';
import { Box, Typography, Chip, Tooltip, Button } from '@mui/material';
import { Iconify } from 'src/components';

const REASON_ICONS = {
    NO_RESPONSE: 'eva:phone-off-outline',
    TOO_EXPENSIVE: 'eva:pricetags-outline',
    CHANGED_MIND: 'eva:refresh-outline',
    ANOTHER_PRACTICE: 'eva:home-outline',
    PRACTICE_TOO_FAR: 'eva:pin-outline',
    CLINICALLY_NOT_SUITABLE: 'eva:close-circle-outline',
    DUPLICATE_ENTRY: 'eva:copy-outline',
    WANT_NHS: 'eva:shield-outline',
    APPOINTMENT_RELATED: 'eva:calendar-outline',
    TREATMENT_RELATED: 'eva:activity-outline',
    OTHER: 'eva:more-horizontal-outline',
};

const MAX_VISIBLE = 5;

const ArchivedLeadsBreakdown = ({ data, total, onReasonClick, onSeeAllClick }) => {
    const visibleData = data.slice(0, MAX_VISIBLE);
    const columnCount = Math.min(visibleData.length, MAX_VISIBLE);

    return (
    <Box sx={{ mb: 3 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Tooltip
                title="Top reasons why leads were archived. Click a reason to see affected leads."
                placement="bottom"
                arrow
                slotProps={{ tooltip: { sx: { bgcolor: '#1e293b', fontSize: '12px', maxWidth: 280 } } }}
            >
                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, cursor: 'help' }}>
                    <Typography sx={{ fontSize: '20px', fontWeight: 600 }}>
                        Archived Leads by Reason
                    </Typography>
                    <Iconify icon="mdi:information-outline" width={14} sx={{ color: '#94a3b8' }} />
                </Box>
            </Tooltip>
            <Chip
                label={`${total} Total`}
                size="small"
                sx={{ bgcolor: '#fee2e2', color: '#ef4444', fontWeight: 600, fontSize: '13px' }}
            />
        </Box>

        {data.length === 0 ? (
            <Box
                sx={{
                    textAlign: 'center',
                    py: 6,
                    bgcolor: '#fff',
                    borderRadius: '16px',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                }}
            >
                <Typography sx={{ color: '#64748b' }}>
                    No archived leads in this period
                </Typography>
            </Box>
        ) : (
            <>
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: {
                            xs: '1fr',
                            sm: `repeat(${Math.min(columnCount, 2)}, 1fr)`,
                            md: `repeat(${Math.min(columnCount, 3)}, 1fr)`,
                            lg: `repeat(${columnCount}, 1fr)`,
                        },
                        gap: '12px',
                        width: '100%',
                    }}
                >
                    {visibleData.map((item, index) => {
                        const percentage = total > 0 ? ((item.count / total) * 100).toFixed(1) : 0;
                        const icon = REASON_ICONS[item.reason] || REASON_ICONS.OTHER;

                        return (
                            <Box
                                key={item.reason || index}
                                onClick={() => onReasonClick?.(item.reason)}
                                sx={{
                                    bgcolor: '#fff',
                                    borderRadius: '14px',
                                    p: '16px',
                                    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                                    borderLeft: '4px solid #ef4444',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                        boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                                        transform: 'translateY(-2px)',
                                    },
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                                    <Box
                                        sx={{
                                            width: 30,
                                            height: 30,
                                            borderRadius: '8px',
                                            bgcolor: '#fef2f2',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                        }}
                                    >
                                        <Iconify icon={icon} width={16} sx={{ color: '#ef4444' }} />
                                    </Box>
                                    <Typography
                                        sx={{
                                            fontSize: '13px',
                                            fontWeight: 600,
                                            color: '#334155',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                            flex: 1,
                                        }}
                                        title={item.label}
                                    >
                                        {item.label}
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
                                    <Typography sx={{ fontSize: '22px', fontWeight: 700, color: '#ef4444' }}>
                                        {item.count}
                                    </Typography>
                                    <Typography sx={{ fontSize: '12px', color: '#94a3b8', fontWeight: 500 }}>
                                        {percentage}%
                                    </Typography>
                                </Box>
                            </Box>
                        );
                    })}
                </Box>

                {data.length > MAX_VISIBLE && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                        <Button
                            variant="text"
                            size="small"
                            onClick={onSeeAllClick}
                            endIcon={<Iconify icon="eva:arrow-forward-outline" width={16} />}
                            sx={{
                                textTransform: 'none',
                                color: '#ef4444',
                                fontWeight: 600,
                                fontSize: '13px',
                                '&:hover': { bgcolor: '#fef2f2' },
                            }}
                        >
                            See all {data.length} reasons
                        </Button>
                    </Box>
                )}
            </>
        )}
    </Box>
    );
};

export default ArchivedLeadsBreakdown;

ArchivedLeadsBreakdown.propTypes = {
    data: PropTypes.arrayOf(
        PropTypes.shape({
            reason: PropTypes.string,
            label: PropTypes.string,
            count: PropTypes.number,
        })
    ),
    total: PropTypes.number,
    onReasonClick: PropTypes.func,
    onSeeAllClick: PropTypes.func,
};

ArchivedLeadsBreakdown.defaultProps = {
    data: [],
    total: 0,
    onReasonClick: null,
    onSeeAllClick: null,
};
